<script setup lang="tsx">
import { ref, computed, watch } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useI18n } from 'vue-i18n'
import IconButton from '@/components/shared/IconButton.vue'
import type { Column } from '@/stores/column'
import { formatDate, getDateFormat } from '@/services/utils'
import { DueDateOnTime, DueDateStr, DueDateOverdue, MissingStr, UpdatedAtStr, Older } from '@/models/enums-generated'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()

const ONE_MONTH = 30
const THREE_MONTHS = 90
const HALF_YEAR = 182
const ONE_YEAR = 365

const { t } = useI18n()
const model = defineModel<Column>({ required: true })
const today = new Date()
const selectedQuick = ref<string | number | null>(null)
const daysExceeded = ref<number>(0)
const daysRemaining = ref<number>(0)

const modelName = computed(() => model.value?.name || '')
const labelText = computed(() => model.value?.label || t('filters.select'))

const timePeriodMapping = {
	month: ONE_MONTH,
	threeMonths: THREE_MONTHS,
	halfYear: HALF_YEAR,
	year: ONE_YEAR,
}

const quickSelectOptions = computed(() =>
	modelName.value === DueDateStr
		? [
				{ label: t('dateRange.ontime'), value: DueDateOnTime },
				{ label: t('dateRange.dueIn', { timePeriod: ONE_MONTH }), value: ONE_MONTH },
				{ label: t('dateRange.dueIn', { timePeriod: THREE_MONTHS }), value: THREE_MONTHS },
				{ label: t('dateRange.dueIn', { timePeriod: HALF_YEAR }), value: HALF_YEAR },
				{ label: t('dateRange.overdue'), value: DueDateOverdue },
				{ label: t('dateRange.overduePeriod', { timePeriod: ONE_MONTH }), value: -ONE_MONTH },
				{ label: t('dateRange.missing'), value: MissingStr },
			]
		: [
				...['month', 'threeMonths', 'halfYear', 'year'].map((p) => ({
					label: t('dateRange.olderThan', { timePeriod: t(`dateRange.${p}`) }),
					value: timePeriodMapping[p as keyof typeof timePeriodMapping],
				})),
				{ label: t('dateRange.overduePeriod', { timePeriod: ONE_MONTH }), value: -ONE_MONTH },
				{ label: t('dateRange.overduePeriod', { timePeriod: THREE_MONTHS }), value: -THREE_MONTHS },
				{ label: t('dateRange.missing'), value: MissingStr },
			],
)

const dateRangeFormatted = computed(() => {
	const [first, second] = dateRange.value
	if (first && isNaN(Date.parse(first))) {
		if (second) {
			const secondDate = new Date(second)
			const timeDiff = today.getTime() - secondDate.getTime()
			const dayDiff = Math.abs(Math.floor(timeDiff / (1000 * 3600 * 24)))
			if (dayDiff > 0) {
				if (first === Older) return t('dateRange.olderThan', { timePeriod: `${dayDiff} ${t('date.day', 2)}` })
				else return t(`dateRange.${first}`)
			}
		}

		return t(`dateRange.${first}`)
	}

	if (first && second && first !== 'Invalid Date' && second !== 'Invalid Date') {
		return [formatDate(first), formatDate(second)].join(' - ')
	}

	return labelText.value
})

const updateDateRange = () => {
	let startDate = new Date()
	let endDate = new Date()
	daysExceeded.value = Math.abs(daysExceeded.value)
	daysRemaining.value = Math.abs(daysRemaining.value)
	if (modelName.value === UpdatedAtStr && daysRemaining.value > 0) {
		startDate.setDate(today.getDate() - daysRemaining.value)
		dateRange.value = [Older, startDate]
	} else {
		endDate.setDate(today.getDate() + daysRemaining.value)
		startDate.setDate(today.getDate() - daysExceeded.value)
		dateRange.value = [startDate, endDate]
	}
}

watch(daysExceeded, (newVal) => {
	if (modelName.value === UpdatedAtStr && newVal > 0) {
		daysRemaining.value = 0
	}
})

watch(daysRemaining, (newVal) => {
	if (modelName.value === UpdatedAtStr && newVal > 0) {
		daysExceeded.value = 0
	}
})

const setQuickSelection = (option: { label: string; value: string | number }) => {
	selectedQuick.value = option.value
	let startDate = new Date()
	let endDate = new Date()

	if (modelName.value === UpdatedAtStr && typeof option.value === 'number' && option.value > 0) {
		startDate.setDate(startDate.getDate() - option.value)
		dateRange.value = [Older, startDate]
	} else {
		if (typeof option.value === 'string') {
			dateRange.value = [option.value, null]
		} else {
			if (option.value < 0) {
				startDate.setDate(startDate.getDate() + option.value)
			} else {
				endDate.setDate(endDate.getDate() + option.value)
			}
			dateRange.value = [startDate, endDate]
		}
	}
	updateDaysFields(option.value)
}

const updateDaysFields = (value: string | number) => {
	if (typeof value !== 'number') return
	daysExceeded.value = Math.abs(value < 0 ? Number(value) : 0)
	daysRemaining.value = value > 0 ? Number(value) : 0
}

const clearFilters = () => {
	dateRange.value = [null, null]
	selectedQuick.value = null
	daysExceeded.value = daysRemaining.value = 0
	model.value.filter = []
}

const closeDropdown = () => {
	document.body.click()
}

const dateRange = computed({
	get: () => {
		const filter = model.value.filter
		if (!Array.isArray(filter) || !filter.length) return [today]
		const [start, end] = filter
		if (typeof start === 'string') {
			return [start, end || today]
		}
		const isStartValid = !isNaN(Date.parse(start))
		const isEndValid = !isNaN(Date.parse(end))
		if (isStartValid && isEndValid) return [start, end]
		if (isStartValid) return [start, null]
		return [today]
	},

	set: (newValue) => {
		if (!Array.isArray(newValue) || !newValue.length) {
			model.value.filter = []
			return
		}
		const [start, end] = newValue
		if (typeof start === 'string') {
			if (end) model.value.filter = [start, end.toISOString().split('T')[0]]
			else model.value.filter = [start]
			return
		}
		const isStartValid = !isNaN(Date.parse(start))
		const isEndValid = !isNaN(Date.parse(end))

		if (isStartValid && isEndValid) {
			model.value.filter = [start, end].map((d) => d.toISOString().split('T')[0])
		} else if (isStartValid) {
			model.value.filter = [start]
		}
	},
})
</script>

<template>
	<div class="dropdown">
		<div
			id="date-picker-filter"
			class="w-100 dropdown-toggle no-arrow"
			data-bs-auto-close="outside"
			type="button"
			data-bs-toggle="dropdown"
			aria-expanded="false"
		>
			<input
				:id="'calendar-filter' + labelText"
				type="text"
				class="form-control with-icon"
				:placeholder="labelText"
				readonly
				:value="dateRangeFormatted"
			/>
			<i class="bi bi-calendar icon-inside"></i>
		</div>
		<div class="dropdown-menu gap-3 rounded-3 shadow-lg p-3">
			<div class="d-flex justify-content-between align-items-center py-0">
				<h4 class="fs-6 m-0">{{ labelText }}</h4>
				<button class="btn btn-close close" :title="t('misc.cancel')" @click="closeDropdown"></button>
			</div>
			<div class="border-top mt-0 mb-2"></div>
			<div class="d-flex w-100 mb-2">
				<Datepicker
					v-model="dateRange"
					range
					:teleport="true"
					:clearable="true"
					:enable-time-picker="false"
					inline
					:format="getDateFormat()"
					:auto-apply="true"
					:dark="theme === 'dark'"
				/>
				<div class="border-left border-light mx-1"></div>
				<div class="d-flex flex-column w-100">
					<div class="d-flex flex-column mb-2">
						<div class="d-flex justify-content-evenly align-items-center gap-2 flex-wrap-nowrap">
							<div class="text-center">
								<label class="field-label">{{ t('dateRange.daysExceeded') }}</label>
								<input type="number" v-model="daysExceeded" class="form-control input-number" @input="updateDateRange" />
							</div>
							<template v-if="modelName === DueDateStr">
								<div class="text-center">
									<label class="field-label">{{ t('dateRange.todaysDate') }}</label>
									<span class="field-span">{{ formatDate(today) }}</span>
								</div>
							</template>
							<div class="text-center">
								<template v-if="modelName === DueDateStr"
									><label class="field-label">{{ t('dateRange.daysRemaining') }}</label></template
								>
								<template v-else
									><label class="field-label">{{ t('dateRange.olderThan') }}</label></template
								>
								<input type="number" v-model="daysRemaining" class="form-control input-number" @input="updateDateRange" />
							</div>
						</div>
					</div>
					<div class="d-flex flex-column gap-1">
						<button
							v-for="option in quickSelectOptions"
							:key="option.value"
							class="btn btn-outline-secondary"
							:class="{ selected: selectedQuick === option.value }"
							@click="setQuickSelection(option)"
						>
							{{ option.label }}
						</button>
					</div>
				</div>
			</div>
			<div class="border-top mb-2"></div>
			<div class="d-flex justify-content-between">
				<IconButton class="bi bi-arrow-repeat" @click="clearFilters" />
				<div class="d-flex align-items-center">
					<span class="text-muted small">{{ dateRangeFormatted }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
:deep(.dp__menu)
    width: 280px
    padding: 12px
    font-size: 0.9rem

:deep(.dp__input)
    font-size: 0.9rem
    height: 50px
    padding: 6px

:deep(.dp__cell_inner)
    font-size: 0.9rem
    width: 38px
    height: 50px

.field-label
    font-size: 12px
    font-weight: normal
    display: block
    margin-bottom: 4px
    gap: 16px

.field-span
    font-size: 12px
    gap: 16px

.input-number
    width: 100px
    font-size: 12px
    margin-bottom: 0
    gap: 16px

.selected
    background-color: #007bff
    color: white

.with-icon
    padding-right: 2.5rem
    color: var(--bs-secondary)
    position: relative

.no-arrow::after
    display: none

.icon-inside
    position: absolute
    right: 0.75rem
    top: 50%
    transform: translateY(-50%)
    pointer-events: none
    font-size: 1rem
    color: var(--bs-secondary)
</style>
